import {isFeatureEnabled} from '@github-ui/feature-flags'
import {VIEW_IDS, VIEW_NAMES} from '@github-ui/issue-url-helper/constants/view-constants'
import {IssueFeedbackDialog, type IssueFeedbackDialogRef} from '@github-ui/issue-viewer/IssueFeedbackDialog'
import {JobInfoWithSubscription} from '@github-ui/issues-bulk-actions/JobInfoWithSubscription'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'
import {useParams} from '@github-ui/react-router'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {useIssuesSidebarState} from '@github-ui/repos-issues-sidebar/use-issues-sidebar-state'
import {Link} from '@primer/react'
import {useEffect, useRef} from 'react'
import {
  type EntryPointComponent,
  graphql,
  type PreloadedQuery,
  useLazyLoadQuery,
  usePreloadedQuery,
  type UseQueryLoaderLoadQueryOptions,
} from 'react-relay'

import {CallToActionItem} from '../components/CallToActionItem'
import {FirstTimeContributionBanner} from '../components/firstTimeContributionBanner/FirstTimeContributionBanner'
import {PinnedIssues} from '../components/pinnedIssues/PinnedIssues'
import {SearchBarActions} from '../components/search/SearchBarActions'
import MobileNavigation from '../components/sidebar/MobileNavigation'
import {RepoIssuesPageLayout} from '../components/sidebar/RepoIssuesPageLayout'
import {StaleCacheBanner} from '../components/StaleCacheBanner'
import {useQueryBulk, useQueryContext} from '../contexts/QueryContext'
import {useEntryPointsLoader} from '../hooks/use-entrypoint-loaders'
import {usePreheatSidebarQueries} from '../hooks/use-preheat-sidebar-queries'
import {useRouterScrollRestoration} from '../hooks/use-router-scroll-restoration'
import {
  IssueRepoNewChoosePage,
  IssueRepoNewPage,
  RepositoryLabelIndexPage,
  RepositoryMilestoneIndexPage,
  RepositoryMilestonePage,
} from '../lazy-routes'
import {preloadWhenIdle} from '../utils/preload-when-idle'
import type {ClientSideRelayDataGeneratorViewQuery} from './__generated__/ClientSideRelayDataGeneratorViewQuery.graphql'
import type {IssueIndexPageQuery, IssueIndexPageQuery$variables} from './__generated__/IssueIndexPageQuery.graphql'
import {AnalyticsWrapper} from './AnalyticsWrapper'
import {currentViewQuery as currentView} from './ClientSideRelayDataGenerator'
import styles from './IssueIndexPage.module.css'
import {List} from './List'

// VIEW_IDS values don't share keys with VIEW_NAMES (e.g. VIEW_IDS.assignedToMe = 'assigned'
// but VIEW_NAMES.assignedToMe = 'Assigned to me'), so flip VIEW_IDS once to look up by value.
const VIEW_NAME_BY_LINK: Record<string, string> = Object.fromEntries(
  Object.entries(VIEW_IDS)
    .filter(([key]) => key in VIEW_NAMES)
    .map(([key, value]) => [value, VIEW_NAMES[key as keyof typeof VIEW_NAMES]]),
)

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query IssueIndexPageQuery(
    $query: String = "state:open archived:false assignee:@me sort:updated-desc"
    $first: Int = 25
    $labelPageSize: Int = 20
    $skip: Int = null
    $name: String!
    $owner: String!
    $type: SearchType = ISSUE_ADVANCED
  ) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      owner {
        userNameDisplayConfiguration
      }
      isWritable
      isOwnerEnterpriseManaged
      ...PinnedIssues
      ...ListRepositoryFragment
      ...FirstTimeContributionBanner
      ...ListQuery
        @arguments(
          query: $query
          first: $first
          labelPageSize: $labelPageSize
          skip: $skip
          fetchRepository: false
          type: $type
        )
    }
    safeViewer {
      # this is intentional to have the current viewer preloaded for the item pickers
      __typename
      id
      login
      name
      profileResourcePath
      avatarUrl(size: 64)
    }
  }
`

export const IssueIndexPage: EntryPointComponent<{pageQuery: IssueIndexPageQuery}, Record<string, never>> = ({
  queries: {pageQuery},
}) => {
  const {queryRef, loadQuery} = useEntryPointsLoader(pageQuery, PageQuery)

  const {setCurrentViewId} = useQueryContext()

  useEffect(() => {
    setCurrentViewId(VIEW_IDS.repository)
  }, [pageQuery, setCurrentViewId])

  if (!queryRef) return null

  return <IssueIndexPageContent pageQueryRef={queryRef} loadQuery={loadQuery} />
}

function IssueIndexPageContent({
  pageQueryRef,
  loadQuery,
}: {
  pageQueryRef: PreloadedQuery<IssueIndexPageQuery>
  loadQuery: (variables: IssueIndexPageQuery$variables, options?: UseQueryLoaderLoadQueryOptions | undefined) => void
}) {
  const {owner, repo} = useParams<{owner: string; repo: string}>()
  useRouterScrollRestoration()
  const {preheatAll} = usePreheatSidebarQueries(owner, repo)
  const issuePinnedViewsEnabled = isFeatureEnabled('issue_pinned_views')

  const currentViewNode = useLazyLoadQuery<ClientSideRelayDataGeneratorViewQuery>(
    currentView,
    {id: VIEW_IDS.repository},
    {fetchPolicy: 'store-only'},
  )

  useEffect(() => {
    return preloadWhenIdle(() => {
      IssueRepoNewPage.preload()
      IssueRepoNewChoosePage.preload()
      // When issue_pinned_views is on, the sidebar hook covers these.
      // Remove this block when the flag is cleaned up.
      if (!isFeatureEnabled('issue_pinned_views')) {
        RepositoryMilestoneIndexPage.preload()
        RepositoryLabelIndexPage.preload()
        RepositoryMilestonePage.preload()
      }
    })
  }, [])

  useEffect(() => {
    if (!issuePinnedViewsEnabled) return
    return preloadWhenIdle(() => {
      preheatAll()
    })
  }, [issuePinnedViewsEnabled, preheatAll])

  const {bulkJobId, setBulkJobId, triggerBulkJobSuccess} = useQueryBulk()
  const feedbackDialogRef = useRef<IssueFeedbackDialogRef>(null)
  const showFeedback = isFeatureEnabled('issues_react_ui_feedback')

  const {selectedLink} = useIssuesSidebarState()
  const showPinnedIssues = !issuePinnedViewsEnabled || selectedLink === VIEW_IDS.repository || selectedLink === ''

  const pageData = usePreloadedQuery<IssueIndexPageQuery>(PageQuery, pageQueryRef)
  const repositoryData = pageData?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  const repository = okResult?.value ?? null

  if (!repository) {
    return null
  }

  const headerActions = isFeatureEnabled('repo_issues_sidebar_layout') ? (
    <SearchBarActions
      variant="pageHeader"
      isWritable={repository.isWritable ?? null}
      isOwnerEnterpriseManaged={repository.isOwnerEnterpriseManaged ?? false}
    />
  ) : undefined

  const pageTitle =
    selectedLink === VIEW_IDS.repository ? 'All issues' : (selectedLink && VIEW_NAME_BY_LINK[selectedLink]) || 'Issues'

  return (
    <AnalyticsWrapper category="Issues Index">
      <StaleCacheBanner />
      <UserNameDisplayContextProvider value={repository.owner.userNameDisplayConfiguration}>
        <JobInfoWithSubscription bulkJobId={bulkJobId} setBulkJobId={setBulkJobId} onSuccess={triggerBulkJobSuccess}>
          <RepoIssuesPageLayout title={pageTitle} actions={headerActions}>
            <div className={styles.middlePaneGrid}>
              <CallToActionItem />
              <FirstTimeContributionBanner repository={repository} />
              {showPinnedIssues && <PinnedIssues repository={repository} />}
              {currentViewNode.node ? (
                <List
                  fetchedView={currentViewNode.node}
                  fetchedRepository={repository}
                  search={repository}
                  loadSearchQuery={loadQuery}
                />
              ) : null}
            </div>
            {showFeedback && (
              <>
                <div className={styles.feedbackLinkContainer}>
                  <Link as="button" inline muted onClick={() => feedbackDialogRef.current?.openDialog()}>
                    Give feedback
                  </Link>
                </div>
                <IssueFeedbackDialog ref={feedbackDialogRef} />
              </>
            )}
          </RepoIssuesPageLayout>
          <MobileNavigation />
        </JobInfoWithSubscription>
      </UserNameDisplayContextProvider>
    </AnalyticsWrapper>
  )
}
